import {createSelector} from 'reselect';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {AppParametricSelector} from '../../../../helpers/reselectHelpers';
import {getAccounts, getCards} from '../../../data/dataSelectors';
import {getFlatPanels, getPanels} from '../../panelsSelectors';
import {panelIsContactCardPanel} from '../../panelsState';
import {ContactCardPanelId, NewPanelId, PanelTypesEnum} from '../../panelStateBase';
import {ContactCardPanelState} from './contactCardPanelState';

/*
 * Contact card panel selector.
 */

export interface ContactCardPanelProps {
  panelId: ContactCardPanelId;
}

export type ContactCardPanelIdSelector<P> = AppParametricSelector<P, ContactCardPanelId>;

function getContactCardPanelId(_state: unknown, props: ContactCardPanelProps) {
  return props.panelId;
}

export type ContactCardPanelSelector<P> = AppParametricSelector<P, ContactCardPanelState>;

export function makeGetContactCardPanel(): ContactCardPanelSelector<ContactCardPanelProps>;
export function makeGetContactCardPanel<P>(
  getPanelId: ContactCardPanelIdSelector<P>,
): ContactCardPanelSelector<P>;
export function makeGetContactCardPanel(getPanelId: ContactCardPanelIdSelector<any> = getContactCardPanelId) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    if (!panel || panel.type !== PanelTypesEnum.CONTACT_CARD) {
      throw new Error('Contact card panel not found.');
    }

    return panel;
  });
}

/*
 * Contact card panel selector via parent panel id.
 */

export interface ContactCardParentPanelProps {
  parentPanelId?: NewPanelId;
}

export type ContactCardParentPanelIdSelector<P> = AppParametricSelector<P, NewPanelId | undefined>;

function getContactCardParentPanelId(_state: unknown, props: ContactCardParentPanelProps) {
  return props.parentPanelId;
}

type ContactCardPanelOptionalSelector<P> = AppParametricSelector<P, ContactCardPanelState | undefined>;

export function makeGetContactCardPanelFromParentPanelId(): ContactCardPanelOptionalSelector<ContactCardParentPanelProps>;
export function makeGetContactCardPanelFromParentPanelId<P>(
  getParentPanelId: ContactCardParentPanelIdSelector<P>,
): ContactCardPanelOptionalSelector<P>;
export function makeGetContactCardPanelFromParentPanelId(
  getParentPanelId: ContactCardParentPanelIdSelector<any> = getContactCardParentPanelId,
) {
  return createSelector([getFlatPanels, getParentPanelId], (panels, parentPanelId) => {
    if (!parentPanelId) {
      return undefined;
    }
    return panels.filter(panelIsContactCardPanel).find((panel) => panel.parentPanelId === parentPanelId);
  });
}

/*
 * Contact Card Selectors.
 */

export function makeGetContactCardSelectedCard() {
  return createSelector([makeGetContactCardPanel(), getCards], (state, cards) => {
    if (!state.selectedCardId) {
      return undefined;
    }
    return cards[state.selectedCardId];
  });
}

export function makeGetContactCardAccount() {
  return createSelector([makeGetContactCardSelectedCard(), getAccounts], (card, accounts) => {
    if (!card || !card.accountId) {
      return undefined;
    }
    return accounts[card.accountId];
  });
}

export function makeGetContactCardIsLoading() {
  return createSelector([makeGetContactCardPanel()], (state) => state.status === AsyncStatusesEnum.LOADING);
}

export function makeGetContactCardConversationCount() {
  return createSelector([makeGetContactCardPanel()], (panel) => panel.conversationIds?.length);
}

export function makeGetContactCardConversationStatus() {
  return createSelector([makeGetContactCardPanel()], (panel) => panel.conversationStatus);
}
